/* eslint-disable @typescript-eslint/no-explicit-any */
import { Empty } from '@/pages/others/Empty'
import { useFetch } from '@/hooks/useFetch'
import { env } from '@/utils/constants/env'
import React, { useContext } from 'react'
import { FormLowcodeLite, Spinner } from 'alurkerja-ui'
import { TasklistService } from '@/api/tasklist'
import { StartProcessPayloadSchema } from './schema'
import { ModalContext } from '@/pages/tasklist/ModalStartProcess'
import { MyTaskActionContext, TasklistMyTaskContext } from '@/pages/tasklist/TasklistMyTask'
import { MyRequestModalContext } from '@/components/request/ModalRequestForm'
import { InputSelectVariable } from '@/components/tasklist/generatedForm/InputSelectVariable'
import { InputSelectUrl } from '@/components/tasklist/generatedForm/InputSelectUrl'
import { generateCustomSpec } from '@/utils/functions/tasklist/generateCustomSpec'
import { createPayloadGenerateForm } from '@/utils/functions/tasklist/createPayloadGenerateForm'
import { VariableRenderer } from '@/components/tasklist/generatedForm/VariableRenderer'
import { MyTaskContextNew } from '@/pages/task/MyTask'
import customComponents from '@/components/tasklist/forms/customComponents'
import InfoList from '@/components/tasklist/generatedForm/InfoList'
import InputImageUpload from '@/components/tasklist/generatedForm/InputImageUpload'
import InputFileUpload from '@/components/tasklist/generatedForm/InputFileUpload'
import Info from '@/components/tasklist/generatedForm/Info'
import { CustomComponentContext } from '@/utils/contexts/CustomComponentContext'
import 'flatpickr/dist/themes/material_green.css'
import InputDateFlat from '@/components/tasklist/generatedForm/InputDateFlat'
import { GeneratedCustomField } from '@/utils/types/generatedForm'
import InputMultiFileUpload from '@/components/tasklist/forms/customComponents/shared/InputMultiFileUpload'

export const RenderScreen = ({ processDefinitionKey, taskDefinitionKey }: any) => {
  /**
   * Fetch camunda form for specific process definition key and task definition key
   * @param processDefinitionKey process definition key
   * @param taskDefinitionKey task definition key
   * @returns camunda form data
   */
  const { data, isLoading, isFetching } = useFetch({
    path: env.apiTasklistBaseUrl + `/api/v1/tasklist/${processDefinitionKey}/${taskDefinitionKey}/camunda-form`,
  })

  if (isLoading || isFetching) {
    return (
      <div className="flex gap-4 h-full justify-center items-center">
        <div className="flex gap-2">
          <Spinner className="text-main-blue-alurkerja" />
          Loading...
        </div>
      </div>
    )
  }

  /**
   * If screen for process definition key is not available
   * and camunda form is available, render camunda form
   * otherwise, return message that screen for process definition key is not available
   */
  const processScreen = (Screen as any)[processDefinitionKey]
  if (processScreen === undefined) {
    if ((data as any[]).length !== 0) {
      return (
        <RenderCamundaForm
          taskDefinitionKey={taskDefinitionKey}
          processDefinitionKey={processDefinitionKey}
          camundaForm={data as any[]}
        ></RenderCamundaForm>
      )
    }
    return <div>Belum ada screen untuk {processDefinitionKey}</div>
  }
  if (!processScreen[taskDefinitionKey]) {
    if ((data as any[]).length !== 0) {
      return (
        <RenderCamundaForm
          taskDefinitionKey={taskDefinitionKey}
          processDefinitionKey={processDefinitionKey}
          camundaForm={data as any[]}
        ></RenderCamundaForm>
      )
    }
    return (
      <div>
        Belum ada screen untuk {processDefinitionKey} dan task {taskDefinitionKey}
      </div>
    )
  }

  /**
   * Render screen component for specific process definition key and task definition key
   * @param processDefinitionKey process definition key
   * @param taskDefinitionKey task definition key
   * @returns screen component
   */
  const CurrentScreen = processScreen[taskDefinitionKey]
  return <CurrentScreen processDefinitionKey={processDefinitionKey}></CurrentScreen>
}

export const RenderScreenFromTaskData = ({ processDefinitionKey, task }: any) => {
  /**
   * Render screen from task data
   * @param processDefinitionKey process definition key
   * @param task task data
   * @returns screen component
   */
  if (!task) {
    return <Empty />
  }

  const {
    data,
    isLoading,
    isFetching,
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useFetch({ path: env.apiTasklistBaseUrl + `/api/v1/tasklist/${processDefinitionKey}/${task.id}/camunda-form` })

  /**
   * Render screen from task data
   * @param {string} processDefinitionKey process definition key
   * @param {object} task task data
   * @returns {ReactElement} screen component
   */
  if (isLoading || isFetching) {
    return (
      <div className="flex gap-4 h-full justify-center items-center">
        <div className="flex gap-2">
          <Spinner className="text-main-blue-alurkerja" />
          Loading...
        </div>
      </div>
    )
  }

  const taskDefinitionKey = task.task_definition_key

  /**
   * Render screen from task data if screen for process definition key is available
   * otherwise render camunda form
   * @param {string} processDefinitionKey process definition key
   * @param {object} task task data
   * @returns {ReactElement} screen component
   */
  if (!(Screen as any)[processDefinitionKey]) {
    if ((data as any[]).length !== 0) {
      return (
        <RenderCamundaForm
          taskDefinitionKey={task.id}
          taskData={task}
          processDefinitionKey={processDefinitionKey}
          camundaForm={data as any[]}
        ></RenderCamundaForm>
      )
    }
    return <div>Belum ada screen untuk {processDefinitionKey}</div>
  }

  const processScreen = (Screen as any)[processDefinitionKey]
  /**
   * Render screen from task data if screen for process definition key and task definition key is available
   * otherwise render camunda form
   * @param {string} processDefinitionKey process definition key
   * @param {object} task task data
   * @returns {ReactElement} screen component
   */
  if (!processScreen[taskDefinitionKey]) {
    if ((data as any[]).length !== 0) {
      return (
        <RenderCamundaForm
          taskDefinitionKey={task.id}
          taskData={task}
          processDefinitionKey={processDefinitionKey}
          camundaForm={data as any[]}
        ></RenderCamundaForm>
      )
    }
    return (
      <div>
        Belum ada screen untuk {processDefinitionKey} dan task {taskDefinitionKey}
      </div>
    )
  }

  /**
   * Render screen from task data
   * @param {string} processDefinitionKey process definition key
   * @param {object} task task data
   * @returns {ReactElement} screen component
   */
  const CurrentScreen = processScreen[taskDefinitionKey]
  return <CurrentScreen processDefinitionKey={processDefinitionKey} task={task}></CurrentScreen>
}

const RenderCamundaForm = ({
  processDefinitionKey,
  taskDefinitionKey,
  camundaForm,
  taskData,
}: {
  processDefinitionKey: string
  taskDefinitionKey: any
  camundaForm: any[]
  taskData?: any
}) => {
  const { closeModal } = useContext(ModalContext)
  const { refetchTasklistMyTask } = useContext(MyTaskActionContext)
  const { resetSelectedTask } = useContext(TasklistMyTaskContext)
  const { refetchMyTask, resetSelectedTask: resetSelectedTaskMyTask } = useContext(MyTaskContextNew)

  const { fromMyRequest, closeModal: closeModalForm } = useContext(MyRequestModalContext)

  let alurkerjaSpec: any[] = []
  if (camundaForm) {
    const updatedCamundaForm = camundaForm.map(field => {
      if (field.type.name === 'INPUT_URL') {
        return {
          ...field,
          type: {
            ...field.type,
            name: 'INPUT_TEXT',
          },
          type_name: 'INPUT_TEXT',
        }
      }
      return field
    })
    const SpecData = generateCustomSpec(updatedCamundaForm, taskData)
    alurkerjaSpec = SpecData
  }

  const { mutate, isLoading: isLoadingStart } = TasklistService.useStartProcess({
    onSuccess: () => {
      refetchTasklistMyTask?.()
      refetchMyTask?.()

      fromMyRequest && closeModalForm()
      closeModal?.()
    },
  })

  const { mutate: completeTask, isLoading: isLoadingComplete } = TasklistService.useCompleteTask({
    onSuccess: () => {
      // from tasklist my task
      refetchTasklistMyTask?.()
      resetSelectedTask?.()

      // from my task
      refetchMyTask?.()
      resetSelectedTaskMyTask?.()
    },
  })

  const generateCustomFields = (
    { fieldSpec, index, setValue, control, watch, ...rest }: GeneratedCustomField,
    defaultRenderer: any,
  ) => {
    if (fieldSpec.form_field_type === 'VARIABLE_RENDERER') {
      return <VariableRenderer fieldSpec={fieldSpec} taskData={taskData} />
    } else if (fieldSpec.form_field_type === 'INPUT_SELECT_VARIABLE') {
      return <InputSelectVariable fieldSpec={fieldSpec} taskData={taskData} setValue={setValue} control={control} />
    } else if (fieldSpec.form_field_type === 'INPUT_SELECT_URL') {
      return <InputSelectUrl fieldSpec={fieldSpec} taskData={taskData} setValue={setValue} control={control} />
    } else if (fieldSpec.form_field_type === 'INPUT_IMAGE_UPLOAD') {
      return <InputImageUpload fieldSpec={fieldSpec} control={control} setValue={setValue} />
    } else if (fieldSpec.form_field_type === 'INPUT_FILE_UPLOAD') {
      return <InputFileUpload fieldSpec={fieldSpec} control={control} setValue={setValue} />
    } else if (fieldSpec.form_field_type === 'INPUT_MULTI_FILE_UPLOAD') {
      return <InputMultiFileUpload fieldSpec={fieldSpec} control={control} setValue={setValue} />
    } else if (fieldSpec.form_field_type === 'CUSTOM_COMPONENT') {
      if (fieldSpec.properties?.NAME in customComponents) {
        return (
          <CustomComponentContext.Provider
            value={{
              processDefinitionKey,
              fieldSpec,
              taskData,
              setValue,
              control,
              watch,
              ...rest,
            }}
          >
            {React.createElement(customComponents[fieldSpec.properties.NAME as keyof typeof customComponents])}
          </CustomComponentContext.Provider>
        )
      }
    } else if (fieldSpec.form_field_type === 'INFO_LIST') {
      return <InfoList fieldSpec={fieldSpec} taskData={taskData} />
    } else if (fieldSpec.form_field_type === 'INFO') {
      return <Info fieldSpec={fieldSpec} />
    } else if (fieldSpec.form_field_type === 'INPUT_DATE') {
      return <InputDateFlat fieldSpec={fieldSpec} setValue={setValue} control={control} />
    } else return defaultRenderer(fieldSpec, index)
  }

  const onSubmit = async (payload: any) => {
    createPayloadGenerateForm(payload, alurkerjaSpec).then(body => {
      if (taskDefinitionKey == 'STARTPROCESS') {
        mutate({
          processDefinitionKey,
          variables: body,
        })
      } else {
        completeTask({
          processDefinitionKey,
          taskId: taskDefinitionKey,
          variables: body,
        })
      }
    })
  }

  if (alurkerjaSpec.length === 0) {
    return (
      <div className="flex gap-4 h-full justify-center items-center">
        <div className="flex gap-2">
          <Spinner className="text-main-blue-alurkerja" />
          Loading...
        </div>
      </div>
    )
  }

  return (
    <div>
      <FormLowcodeLite
        inline={false}
        renderFlow="column"
        customField={generateCustomFields}
        baseUrl=""
        spec={alurkerjaSpec}
        isLoading={isLoadingStart || isLoadingComplete}
        hideCancelButton
        buttonPosition="right"
        onSubmit={data => onSubmit(data as StartProcessPayloadSchema)}
      ></FormLowcodeLite>
    </div>
  )
}
